/**
 * Данные вкладки «Типы заявок» (Настройки менеджера, миграция 0068).
 *
 * Тип заявки = категория услуг с is_request_type=true. Управляется поверх
 * admin-CRUD каталога:
 *   GET   /v1/admin/service-catalog/categories            — все категории
 *   POST  /v1/admin/service-catalog/categories            — создать
 *   PATCH /v1/admin/service-catalog/categories/{id}        — правка полей
 *   POST  /v1/admin/service-catalog/categories/{id}/activate|deactivate — архивация
 *
 * Список «типов заявок» = категории с is_request_type=true (фильтр на клиенте,
 * чтобы не плодить меню-категории в этой вкладке). Создание типа = создание
 * категории с is_request_type=true. «Выключить» = выделенный POST .../deactivate
 * (PATCH с is_active НЕ работает — бэк-DTO категории не содержит is_active и
 * молча игнорирует флаг, #77).
 *
 * Server-state только через React Query; после мутаций инвалидируем список.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminCreateServiceCategory,
  adminListServiceCategories,
  adminSetServiceCategoryActive,
  adminUpdateServiceCategory,
} from '@hsc/api-client';
import type {
  ServiceCategoriesListResponse,
  ServiceCategory,
  ServiceCategoryCreateInput,
  ServiceCategoryUpdateInput,
} from '@hsc/types';

const REQ_TYPES_KEY = ['service-catalog', 'categories', 'request-types'] as const;

/** GET всех категорий (фильтр request-type на клиенте). */
export function useRequestTypeCategories() {
  return useQuery<ServiceCategoriesListResponse>({
    queryKey: REQ_TYPES_KEY,
    queryFn: () => adminListServiceCategories(),
    staleTime: 10_000,
  });
}

/** POST — создать тип заявки (категория с is_request_type=true). */
export function useCreateRequestType() {
  const qc = useQueryClient();
  return useMutation<ServiceCategory, Error, ServiceCategoryCreateInput>({
    mutationFn: (input) => adminCreateServiceCategory(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REQ_TYPES_KEY });
      void qc.invalidateQueries({ queryKey: ['service-catalog'] });
    },
  });
}

/** PATCH — правка полей типа заявки (название/иконка/отдел/флаги). */
export function useUpdateRequestType() {
  const qc = useQueryClient();
  return useMutation<ServiceCategory, Error, { id: string; patch: ServiceCategoryUpdateInput }>({
    mutationFn: ({ id, patch }) => adminUpdateServiceCategory(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REQ_TYPES_KEY });
      void qc.invalidateQueries({ queryKey: ['service-catalog'] });
    },
  });
}

/** POST /activate|/deactivate — архивация/разархивация типа заявки (#77). */
export function useSetRequestTypeActive() {
  const qc = useQueryClient();
  return useMutation<ServiceCategory, Error, { id: string; active: boolean }>({
    mutationFn: ({ id, active }) => adminSetServiceCategoryActive(id, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REQ_TYPES_KEY });
      void qc.invalidateQueries({ queryKey: ['service-catalog'] });
    },
  });
}
